/**
 * Entry Hooks
 *
 * Shared React hooks for entry CRUD operations using Convex.
 * These hooks work identically on web and mobile.
 */

import { useQuery, useMutation } from 'convex/react';
import { api } from '@have-done/backend/convex/_generated/api';
import type { Category } from '../types/entry';

/**
 * Get entries for today
 */
export function useTodayEntries() {
  const today = new Date().toISOString().split('T')[0];
  return useQuery(api.entries.listByDate, { date: today });
}

/**
 * Get entries for a specific date
 */
export function useEntriesByDate(date: string) {
  return useQuery(api.entries.listByDate, { date });
}

/**
 * Get entries for a date range
 */
export function useEntriesByDateRange(startDate: string, endDate: string) {
  return useQuery(api.entries.listByDateRange, { startDate, endDate });
}

/**
 * Get entries by category
 */
export function useEntriesByCategory(category: Category, limit?: number) {
  return useQuery(api.entries.listByCategory, { category, limit });
}

/**
 * Get a single entry by ID
 */
export function useEntry(id: string) {
  return useQuery(api.entries.get, { id: id as any });
}

/**
 * Search entries by content
 */
export function useSearchEntries(query: string, limit?: number) {
  return useQuery(api.entries.search, { query, limit });
}

/**
 * Create entry mutation
 */
export function useCreateEntry() {
  return useMutation(api.entries.create);
}

/**
 * Update entry mutation
 */
export function useUpdateEntry() {
  return useMutation(api.entries.update);
}

/**
 * Delete entry mutation
 */
export function useDeleteEntry() {
  return useMutation(api.entries.remove);
}
